// src/types/XoneFieldType.ts
var XONE_FIELD_TYPES = [
  // Cadenas
  "T",
  "TL",
  "THTML",
  "TT",
  "X",
  // Numericos
  "N",
  "N1",
  "N2",
  "N3",
  "N4",
  "N5",
  "N6",
  "NC",
  "F",
  // Logicos / temporales
  "B",
  "D",
  "DT",
  "H",
  // Multimedia / archivos
  "IMG",
  "PH",
  "VD",
  "AT",
  "A",
  "W",
  "DR",
  // Lookup / enumeraciones / otros
  "C",
  "R",
  "E",
  "WEB",
  "Z",
  "M",
  "CAM",
  "ARRAY"
];
function isXoneFieldType(value) {
  return typeof value === "string" && XONE_FIELD_TYPES.includes(value);
}
var STRING_FIELD_TYPES = /* @__PURE__ */ new Set([
  "T",
  "TL",
  "THTML",
  "TT",
  "X",
  "IMG",
  "PH",
  "VD",
  "AT",
  "A",
  "W",
  "DR",
  "E",
  "WEB",
  "Z",
  "CAM"
]);
var NUMERIC_FIELD_TYPES = /* @__PURE__ */ new Set([
  "N",
  "N1",
  "N2",
  "N3",
  "N4",
  "N5",
  "N6",
  "NC"
]);
var DATE_FIELD_TYPES = /* @__PURE__ */ new Set([
  "D",
  "DT",
  "H"
]);
var LOOKUP_FIELD_TYPES = /* @__PURE__ */ new Set([
  "C",
  "R"
]);

// src/errors/XoneTypeError.ts
var XoneTypeError = class _XoneTypeError extends Error {
  code;
  fieldType;
  fieldName;
  constructor(init) {
    super(init.message, init.cause !== void 0 ? { cause: init.cause } : void 0);
    this.name = "XoneTypeError";
    this.code = init.code;
    if (init.fieldType !== void 0) this.fieldType = init.fieldType;
    if (init.fieldName !== void 0) this.fieldName = init.fieldName;
    Object.setPrototypeOf(this, _XoneTypeError.prototype);
  }
};

// src/conversion/coerce/array.ts
function coerceArray(value) {
  if (value === null || value === void 0) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const s = value.trim();
    if (s.length === 0) return null;
    try {
      const parsed = JSON.parse(s);
      if (!Array.isArray(parsed)) {
        throw new XoneTypeError({
          code: "XONE.TYPE.COERCE_FAIL",
          message: `Cadena '${value}' no representa un array JSON.`,
          fieldType: "ARRAY"
        });
      }
      return parsed;
    } catch (err) {
      if (err instanceof XoneTypeError) throw err;
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `JSON invalido al parsear array: ${err.message}.`,
        fieldType: "ARRAY",
        cause: err
      });
    }
  }
  throw new XoneTypeError({
    code: "XONE.TYPE.COERCE_FAIL",
    message: `Tipo '${typeof value}' no convertible a 'ARRAY'.`,
    fieldType: "ARRAY"
  });
}

// src/conversion/coerce/boolean.ts
function coerceBoolean(value, fieldType) {
  if (value === null || value === void 0) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Numero no finito no convertible a booleano '${fieldType}'.`,
        fieldType
      });
    }
    return value !== 0;
  }
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "") return null;
    if (v === "0" || v === "false" || v === "no") return false;
    return true;
  }
  throw new XoneTypeError({
    code: "XONE.TYPE.COERCE_FAIL",
    message: `Valor de tipo '${typeof value}' no convertible a booleano '${fieldType}'.`,
    fieldType
  });
}

// src/conversion/coerce/date.ts
var ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
var ISO_FULL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;
var HHMM = /^(\d{1,2}):(\d{2})(:(\d{2}))?$/;
function coerceDate(value, fieldType) {
  if (value === null || value === void 0) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Date invalida asignada a campo '${fieldType}'.`,
        fieldType
      });
    }
    return normalizeDate(new Date(value.getTime()), fieldType);
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Numero no finito no convertible a fecha '${fieldType}'.`,
        fieldType
      });
    }
    return normalizeDate(new Date(value), fieldType);
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (s.length === 0) return null;
    const parsed = parseDateString(s, fieldType);
    if (parsed === null) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Cadena '${value}' no es una fecha ISO valida para '${fieldType}'.`,
        fieldType
      });
    }
    return parsed;
  }
  throw new XoneTypeError({
    code: "XONE.TYPE.COERCE_FAIL",
    message: `Valor de tipo '${typeof value}' no convertible a fecha '${fieldType}'.`,
    fieldType
  });
}
function parseDateString(s, fieldType) {
  if (fieldType === "H") {
    const m = HHMM.exec(s);
    if (m) {
      const h = Number.parseInt(m[1] ?? "0", 10);
      const mm = Number.parseInt(m[2] ?? "0", 10);
      const ss = Number.parseInt(m[4] ?? "0", 10);
      if (h > 23 || mm > 59 || ss > 59) return null;
      const d = new Date(Date.UTC(1970, 0, 1, h, mm, ss, 0));
      return d;
    }
  }
  if (ISO_DATE_ONLY.test(s)) {
    const [y, m, d] = s.split("-").map((x) => Number.parseInt(x, 10));
    if (y === void 0 || m === void 0 || d === void 0 || !Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    const date = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
    if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
      return null;
    }
    return normalizeDate(date, fieldType);
  }
  if (ISO_FULL.test(s)) {
    const t = Date.parse(s);
    if (Number.isNaN(t)) return null;
    return normalizeDate(new Date(t), fieldType);
  }
  return null;
}
function normalizeDate(d, fieldType) {
  if (fieldType === "D") {
    return new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)
    );
  }
  if (fieldType === "H") {
    return new Date(
      Date.UTC(1970, 0, 1, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), 0)
    );
  }
  return d;
}

// src/conversion/coerce/map.ts
function coerceMap(value) {
  if (value === null || value === void 0) return null;
  if (typeof value === "object" && !(value instanceof Date) && !Array.isArray(value)) {
    const obj = value;
    const lat = obj["lat"];
    const lon = obj["lon"];
    if (typeof lat !== "number" || typeof lon !== "number") {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: "Mapa requiere propiedades numericas `lat` y `lon`.",
        fieldType: "M"
      });
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: "lat/lon no finitos en valor de tipo `M`.",
        fieldType: "M"
      });
    }
    return { ...obj, lat, lon };
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (s.length === 0) return null;
    const sep = s.includes(";") ? ";" : ",";
    const parts = s.split(sep);
    if (parts.length < 2) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Cadena '${value}' no es un mapa valido (esperado 'lat;lon' o 'lat,lon').`,
        fieldType: "M"
      });
    }
    const lat = Number.parseFloat((parts[0] ?? "").trim());
    const lon = Number.parseFloat((parts[1] ?? "").trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Cadena '${value}' no contiene lat/lon numericos.`,
        fieldType: "M"
      });
    }
    return { lat, lon };
  }
  throw new XoneTypeError({
    code: "XONE.TYPE.COERCE_FAIL",
    message: `Tipo '${typeof value}' no convertible a mapa 'M'.`,
    fieldType: "M"
  });
}

// src/numeric/decimalsFromType.ts
function decimalsFromType(fieldType) {
  switch (fieldType) {
    case "N":
    case "NC":
      return 0;
    case "N1":
      return 1;
    case "N2":
      return 2;
    case "N3":
      return 3;
    case "N4":
      return 4;
    case "N5":
      return 5;
    case "N6":
      return 6;
    default:
      return null;
  }
}

// src/numeric/safeRound.ts
function safeRound(value, decimals) {
  if (!Number.isFinite(value)) return value;
  const d = Number.isFinite(decimals) ? Math.max(0, Math.trunc(decimals)) : 0;
  if (value === 0) return 0;
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  const s = abs.toString();
  const [intPart, fracPartRaw = ""] = s.split(".");
  const fracPart = fracPartRaw;
  if (fracPart.length <= d) return sign * abs;
  const allDigits = (intPart ?? "") + fracPart;
  const intLen = (intPart ?? "").length;
  const cutAt = intLen + d;
  const kept = allDigits.slice(0, cutAt);
  const roundDigit = Number.parseInt(allDigits.charAt(cutAt) ?? "0", 10);
  const remainder = allDigits.slice(cutAt + 1);
  let bump = false;
  if (roundDigit > 5) {
    bump = true;
  } else if (roundDigit < 5) {
    bump = false;
  } else {
    const hasMore = /[1-9]/.test(remainder);
    if (hasMore) {
      bump = true;
    } else {
      const lastKeptStr = kept.charAt(kept.length - 1) || "0";
      const lastKept = Number.parseInt(lastKeptStr, 10);
      bump = lastKept % 2 === 1;
    }
  }
  let resultDigits = kept.length === 0 ? "0" : kept;
  if (bump) {
    const arr = resultDigits.split("");
    let i = arr.length - 1;
    while (i >= 0) {
      const c = arr[i] ?? "0";
      const n = Number.parseInt(c, 10) + 1;
      if (n < 10) {
        arr[i] = String(n);
        break;
      }
      arr[i] = "0";
      i--;
    }
    if (i < 0) arr.unshift("1");
    resultDigits = arr.join("");
  }
  let finalStr;
  if (d === 0) {
    finalStr = resultDigits;
  } else {
    const newIntLen = resultDigits.length - d;
    if (newIntLen <= 0) {
      finalStr = "0." + "0".repeat(-newIntLen) + resultDigits;
    } else {
      finalStr = resultDigits.slice(0, newIntLen) + "." + resultDigits.slice(newIntLen);
    }
  }
  const numeric = Number.parseFloat(finalStr);
  return sign * numeric;
}

// src/conversion/coerce/number.ts
function coerceNumber(value, fieldType) {
  if (value === null || value === void 0) return null;
  let n;
  if (typeof value === "number") {
    n = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const normalized = trimmed.replace(",", ".");
    n = Number(normalized);
    if (!Number.isFinite(n)) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `No se puede convertir la cadena '${value}' a numerico '${fieldType}'.`,
        fieldType
      });
    }
  } else if (typeof value === "boolean") {
    n = value ? 1 : 0;
  } else if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Date invalida no convertible a numerico '${fieldType}'.`,
        fieldType
      });
    }
    n = value.getTime();
  } else {
    throw new XoneTypeError({
      code: "XONE.TYPE.COERCE_FAIL",
      message: `Valor de tipo '${typeof value}' no convertible a numerico '${fieldType}'.`,
      fieldType
    });
  }
  if (!Number.isFinite(n)) {
    throw new XoneTypeError({
      code: "XONE.TYPE.COERCE_FAIL",
      message: `Numerico no finito asignado a '${fieldType}'.`,
      fieldType
    });
  }
  const decimals = decimalsFromType(fieldType);
  if (decimals === null) {
    return n;
  }
  if (decimals === 0) {
    return Math.trunc(n);
  }
  return safeRound(n, decimals);
}

// src/conversion/coerce/text.ts
function coerceText(value, fieldType, meta) {
  if (value === null || value === void 0) return null;
  let str;
  if (typeof value === "string") {
    str = value;
  } else if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `No se puede convertir un numero no finito a campo '${fieldType}'.`,
        fieldType
      });
    }
    str = String(value);
  } else if (typeof value === "boolean") {
    str = value ? "1" : "0";
  } else if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new XoneTypeError({
        code: "XONE.TYPE.COERCE_FAIL",
        message: `Date invalida no convertible a campo '${fieldType}'.`,
        fieldType
      });
    }
    str = value.toISOString();
  } else {
    throw new XoneTypeError({
      code: "XONE.TYPE.COERCE_FAIL",
      message: `Valor de tipo '${typeof value}' no convertible a campo textual '${fieldType}'.`,
      fieldType
    });
  }
  if (meta?.fieldsize !== void 0 && meta.fieldsize > 0 && str.length > meta.fieldsize) {
    str = str.substring(0, meta.fieldsize);
  }
  return str;
}

// src/conversion/fromStorage.ts
function fromStorage(raw, fieldType, meta) {
  if (!isXoneFieldType(fieldType)) {
    throw new XoneTypeError({
      code: "XONE.TYPE.UNKNOWN_TYPE",
      message: `Tipo de campo XOne desconocido: '${String(fieldType)}'.`
    });
  }
  if (raw === null || raw === void 0) return null;
  if (STRING_FIELD_TYPES.has(fieldType)) {
    return coerceText(raw, fieldType, meta);
  }
  if (NUMERIC_FIELD_TYPES.has(fieldType)) {
    return coerceNumber(raw, fieldType);
  }
  switch (fieldType) {
    case "D":
    case "DT":
    case "H":
      return coerceDate(raw, fieldType);
    case "B":
      return coerceBoolean(raw, fieldType);
    case "M":
      return coerceMap(raw);
    case "ARRAY":
      return coerceArray(raw);
    case "C":
    case "R":
      if (typeof raw === "string" || typeof raw === "number") return raw;
      if (typeof raw === "boolean") return raw ? 1 : 0;
      return coerceText(raw, fieldType, meta);
    case "F": {
      if (typeof raw === "number" || typeof raw === "boolean") return raw;
      if (raw instanceof Date) return raw;
      if (typeof raw === "string") return raw;
      return String(raw);
    }
    default:
      throw new XoneTypeError({
        code: "XONE.TYPE.UNKNOWN_TYPE",
        message: `Tipo XOne no manejado en fromStorage: '${String(fieldType)}'.`
      });
  }
}

// src/conversion/toStorage.ts
function toStorage(value, fieldType, meta) {
  if (!isXoneFieldType(fieldType)) {
    throw new XoneTypeError({
      code: "XONE.TYPE.UNKNOWN_TYPE",
      message: `Tipo de campo XOne desconocido: '${String(fieldType)}'.`
    });
  }
  if (value === null || value === void 0) return null;
  if (STRING_FIELD_TYPES.has(fieldType)) {
    return coerceText(value, fieldType, meta);
  }
  if (NUMERIC_FIELD_TYPES.has(fieldType)) {
    return coerceNumber(value, fieldType);
  }
  switch (fieldType) {
    case "D": {
      const d = coerceDate(value, fieldType);
      if (d === null) return null;
      return formatIsoDate(d);
    }
    case "DT": {
      const d = coerceDate(value, fieldType);
      if (d === null) return null;
      return d.toISOString();
    }
    case "H": {
      const d = coerceDate(value, fieldType);
      if (d === null) return null;
      return formatIsoTime(d);
    }
    case "B":
      return coerceBoolean(value, fieldType);
    case "M":
      return coerceMap(value);
    case "ARRAY":
      return coerceArray(value);
    case "C":
    case "R":
      if (typeof value === "string" || typeof value === "number") return value;
      if (typeof value === "boolean") return value ? 1 : 0;
      return coerceText(value, fieldType, meta);
    case "F":
      if (typeof value === "number" || typeof value === "boolean") return value;
      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
          throw new XoneTypeError({
            code: "XONE.TYPE.COERCE_FAIL",
            message: "Date invalida en campo F.",
            fieldType
          });
        }
        return value.toISOString();
      }
      return coerceText(value, fieldType, meta);
    default:
      throw new XoneTypeError({
        code: "XONE.TYPE.UNKNOWN_TYPE",
        message: `Tipo XOne no manejado en toStorage: '${String(fieldType)}'.`
      });
  }
}
function formatIsoDate(d) {
  const y = d.getUTCFullYear().toString().padStart(4, "0");
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatIsoTime(d) {
  const h = d.getUTCHours().toString().padStart(2, "0");
  const m = d.getUTCMinutes().toString().padStart(2, "0");
  const s = d.getUTCSeconds().toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// src/format/variantToString.ts
var V2S_DATEONLY = 1;
var V2S_TIME00 = 2;
function variantToString(value, flags) {
  if (value === null || value === void 0) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    return String(value);
  }
  if (typeof value === "boolean") return value ? "1" : "0";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return formatDate(value, flags ?? 0);
  }
  if (Array.isArray(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  if (typeof value === "object") {
    const obj = value;
    if (typeof obj.lat === "number" && typeof obj.lon === "number") {
      return `${obj.lat};${obj.lon}`;
    }
    try {
      return JSON.stringify(obj);
    } catch {
      return "";
    }
  }
  return String(value);
}
function formatDate(d, flags) {
  const dateOnly = (flags & V2S_DATEONLY) === V2S_DATEONLY;
  const time00 = (flags & V2S_TIME00) === V2S_TIME00;
  const y = d.getUTCFullYear().toString().padStart(4, "0");
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  const datePart = `${y}-${m}-${day}`;
  if (dateOnly) return datePart;
  if (time00) {
    return `${datePart} 00:00:00`;
  }
  return d.toISOString();
}

// src/format/xoneScriptDate.ts
function formatXoneSqlDateLocal(d) {
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear().toString().padStart(4, "0");
  const mo = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const h = d.getHours().toString().padStart(2, "0");
  const mi = d.getMinutes().toString().padStart(2, "0");
  const s = d.getSeconds().toString().padStart(2, "0");
  return `${y}-${mo}-${day} ${h}:${mi}:${s}`;
}
var XoneScriptDate = class extends Date {
  /** Coercion a string (`String(x)`, `x.toString()`) -> SQL local. */
  toString() {
    return formatXoneSqlDateLocal(this);
  }
  [Symbol.toPrimitive](hint) {
    if (hint === "number") return this.getTime();
    return formatXoneSqlDateLocal(this);
  }
};
function wrapScriptDate(d) {
  if (d instanceof XoneScriptDate) return d;
  return new XoneScriptDate(d.getTime());
}
var IS_NATIVE_DATE = (value) => value instanceof Date;
var ScriptDateConstructor = new Proxy(
  XoneScriptDate,
  {
    get(target, key, receiver) {
      if (key === Symbol.hasInstance) return IS_NATIVE_DATE;
      return Reflect.get(target, key, receiver);
    },
    /**
     * `Date()` sin `new`. El nativo devuelve la fecha actual como texto; una
     * clase ES2015 no se puede invocar así y lanzaría `TypeError`, tumbando el
     * script entero. Se devuelve el formato XOne, coherente con el `toString`
     * que ve el proyecto (la legacy aquí devuelve el formato largo, porque el
     * spec no hace pasar `Date()` por `Date.prototype.toString`).
     */
    apply() {
      return formatXoneSqlDateLocal(/* @__PURE__ */ new Date());
    }
  }
);
Object.defineProperty(XoneScriptDate, "name", { value: "Date", configurable: true });
Object.defineProperty(XoneScriptDate.prototype, "constructor", {
  value: ScriptDateConstructor,
  writable: true,
  configurable: true
});

// src/format/xoneDisplayDate.ts
var ISO_DATE_OR_DATETIME = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
var EPOCH_MS = /^-?\d{10,}$/;
var DATE_TYPES = /* @__PURE__ */ new Set(["D", "DT", "H"]);
function pad2(n) {
  return n.toString().padStart(2, "0");
}
function isPlausibleDateParts(m) {
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  if (m[4] !== void 0) {
    const h = Number(m[4]);
    const mi = Number(m[5]);
    const sec = m[6] !== void 0 ? Number(m[6]) : 0;
    if (h > 23 || mi > 59 || sec > 59) return false;
  }
  return true;
}
function assembleFromParts(y, mo, d, h, mi, s, fieldType) {
  if (fieldType === "H") return `${h}:${mi}`;
  if (fieldType === "DT") return `${d}/${mo}/${y} ${h}:${mi}:${s}`;
  return `${d}/${mo}/${y}`;
}
function formatFromDate(d, fieldType) {
  return assembleFromParts(
    d.getUTCFullYear().toString().padStart(4, "0"),
    pad2(d.getUTCMonth() + 1),
    pad2(d.getUTCDate()),
    pad2(d.getUTCHours()),
    pad2(d.getUTCMinutes()),
    pad2(d.getUTCSeconds()),
    fieldType
  );
}
function formatXoneDisplayDate(value, fieldType) {
  if (value === null || value === void 0) return "";
  if (!DATE_TYPES.has(fieldType)) {
    return typeof value === "string" ? value : String(value);
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (s.length === 0) return "";
    const m = ISO_DATE_OR_DATETIME.exec(s);
    if (m && isPlausibleDateParts(m)) {
      const [, y, mo, d, h = "00", mi = "00", sec = "00"] = m;
      return assembleFromParts(y, mo, d, h, mi, sec, fieldType);
    }
    if (EPOCH_MS.test(s)) {
      try {
        const date = coerceDate(Number.parseInt(s, 10), fieldType);
        return date ? formatFromDate(date, fieldType) : "";
      } catch {
        return s;
      }
    }
    return s;
  }
  if (value instanceof Date || typeof value === "number") {
    try {
      const date = coerceDate(value, fieldType);
      return date ? formatFromDate(date, fieldType) : "";
    } catch {
      return String(value);
    }
  }
  return String(value);
}

// src/format/xoneInputDate.ts
var ISO_DATE_INPUT = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;
var INPUT_DATE_TYPES = /* @__PURE__ */ new Set(["date", "datetime-local", "time"]);
function pad22(n) {
  return String(n).padStart(2, "0");
}
function formatXoneInputDate(value, inputType) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    if (INPUT_DATE_TYPES.has(inputType)) {
      const y2 = String(value.getUTCFullYear()).padStart(4, "0");
      const mo2 = pad22(value.getUTCMonth() + 1);
      const d2 = pad22(value.getUTCDate());
      const h2 = pad22(value.getUTCHours());
      const mi2 = pad22(value.getUTCMinutes());
      if (inputType === "time") return `${h2}:${mi2}`;
      if (inputType === "datetime-local") return `${y2}-${mo2}-${d2}T${h2}:${mi2}`;
      return `${y2}-${mo2}-${d2}`;
    }
  }
  const str = value === void 0 || value === null ? "" : String(value);
  if (!INPUT_DATE_TYPES.has(inputType)) return str;
  if (str === "") return "";
  const m = ISO_DATE_INPUT.exec(str);
  if (!m) return str;
  const [, y, mo, d, h = "00", mi = "00"] = m;
  if (inputType === "date") return `${y}-${mo}-${d}`;
  if (inputType === "time") return `${h}:${mi}`;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

// src/rowid/generateRowId.ts
function generateRowId() {
  const cryptoObj = getCrypto();
  const bytes = new Uint8Array(16);
  cryptoObj.getRandomValues(bytes);
  return bytesToHex(bytes);
}
function isRowId(value) {
  return typeof value === "string" && /^[0-9a-fA-F_-]{1,100}$/.test(value);
}
function getCrypto() {
  const g = globalThis;
  if (g.crypto && typeof g.crypto.getRandomValues === "function") {
    return g.crypto;
  }
  throw new Error(
    "XONE.TYPE.ROWID_NO_CRYPTO: el entorno no expone crypto.getRandomValues; no se generaran ROWIDs inseguros. Provea un crypto compatible."
  );
}
function bytesToHex(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i] ?? 0;
    out += byte.toString(16).padStart(2, "0");
  }
  return out;
}

// src/script/hasMeaningfulScript.ts
function hasMeaningfulScript(scriptSource) {
  if (typeof scriptSource !== "string") return false;
  const src = scriptSource;
  if (src.trim() === "") return false;
  let i = 0;
  const n = src.length;
  while (i < n) {
    const c = src[i];
    if (c === " " || c === "	" || c === "\r" || c === "\n" || c === "\f" || c === "\v") {
      i += 1;
      continue;
    }
    if (c === "/" && src[i + 1] === "/") {
      const nl = src.indexOf("\n", i + 2);
      if (nl === -1) return false;
      i = nl + 1;
      continue;
    }
    if (c === "/" && src[i + 1] === "*") {
      const end = src.indexOf("*/", i + 2);
      if (end === -1) return false;
      i = end + 2;
      continue;
    }
    return true;
  }
  return false;
}

// src/nameRules/predicates.ts
var FIELD_NAME_PATTERN = /^[A-Z_][A-Z0-9_]*$/;
var MAP_PREFIX = "MAP_";
var MAP_DIFFERED_PREFIX = "MAP_DIFFERED_";
var ID_PREFIX = "ID_";
var ROWID_NAME = "ROWID";
function isMapField(fieldName) {
  return typeof fieldName === "string" && fieldName.startsWith(MAP_PREFIX);
}
function isMapDifferedField(fieldName) {
  return typeof fieldName === "string" && fieldName.startsWith(MAP_DIFFERED_PREFIX);
}
function isForeignKeyField(fieldName) {
  return typeof fieldName === "string" && fieldName.startsWith(ID_PREFIX);
}
function isReadonlyField(fieldName) {
  if (typeof fieldName !== "string") return false;
  return fieldName === ROWID_NAME;
}
function isNonPersistableField(fieldName) {
  if (typeof fieldName !== "string") return false;
  return isMapField(fieldName) || fieldName === ROWID_NAME;
}
function isRowIdField(fieldName) {
  return fieldName === ROWID_NAME;
}
function normalizeFieldName(name) {
  if (typeof name !== "string") {
    throw new XoneTypeError({
      code: "XONE.TYPE.FIELD_NAME",
      message: "El nombre de campo debe ser string."
    });
  }
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    throw new XoneTypeError({
      code: "XONE.TYPE.FIELD_NAME",
      message: "El nombre de campo no puede estar vacio."
    });
  }
  const upper = trimmed.toLocaleUpperCase("en-US");
  if (!FIELD_NAME_PATTERN.test(upper)) {
    throw new XoneTypeError({
      code: "XONE.TYPE.FIELD_NAME",
      message: `Nombre de campo invalido: '${name}'. Use MAYUSCULAS y '_' (ej: CODIGO, ID_EMPRESA).`,
      fieldName: name
    });
  }
  return upper;
}
function tryNormalizeFieldName(name) {
  try {
    return normalizeFieldName(name);
  } catch {
    return null;
  }
}
function isValidFieldName(name) {
  return typeof name === "string" && FIELD_NAME_PATTERN.test(name);
}
export {
  DATE_FIELD_TYPES,
  LOOKUP_FIELD_TYPES,
  NUMERIC_FIELD_TYPES,
  STRING_FIELD_TYPES,
  ScriptDateConstructor,
  V2S_DATEONLY,
  V2S_TIME00,
  XONE_FIELD_TYPES,
  XoneScriptDate,
  XoneTypeError,
  decimalsFromType,
  formatXoneDisplayDate,
  formatXoneInputDate,
  formatXoneSqlDateLocal,
  fromStorage,
  generateRowId,
  hasMeaningfulScript,
  isForeignKeyField,
  isMapDifferedField,
  isMapField,
  isNonPersistableField,
  isReadonlyField,
  isRowId,
  isRowIdField,
  isValidFieldName,
  isXoneFieldType,
  normalizeFieldName,
  safeRound,
  toStorage,
  tryNormalizeFieldName,
  variantToString,
  wrapScriptDate
};
//# sourceMappingURL=index.js.map